// =============================================================================
// src/app/commands.ts — AppCommands: acoes globais (sendPrompt/openLink/openCopilotChat).
//
// Proveniencia: porte fiel de assets/shared.js (window.sendPrompt + window.openLink)
//   e do contrato de assets/copilot-chat.js (window.openCopilotChat). No mockup,
//   sendPrompt apenas dispara um toast de demonstracao no canto inferior direito
//   (.action-toast, estilizado em assets/shared.css linhas 174-197). Mantemos esse
//   comportamento exato — main.ts depois publica estes metodos no window para os
//   onclick inline da marcacao legada.
//
// SRP: cada metodo faz uma acao. MiniToast encapsula o ciclo mostrar/esconder.
// =============================================================================

import type { AppCommands } from './types';

/** Tempo de exibicao do toast, em ms (shared.js usava 2800). */
const MINI_TOAST_VISIBLE_MS = 2800;

/** Id/classe do elemento de toast — paridade com shared.js (#__actionToast). */
const MINI_TOAST_ID = '__actionToast';
const MINI_TOAST_CLASS = 'action-toast';

/**
 * Toast efemero no canto inferior direito. Encapsula o elemento e o timer para
 * que sucessivas chamadas reaproveitem o mesmo no DOM (sem empilhar elementos).
 */
class MiniToast {
  private element: HTMLDivElement | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  /** Exibe `message` por MINI_TOAST_VISIBLE_MS, reiniciando o timer se ja visivel. */
  show(message: string): void {
    const toast = this.ensureElement();
    toast.textContent = `→ ${message}`;
    toast.classList.add('visible');
    this.restartHideTimer(toast);
  }

  /** Cria (uma unica vez) o elemento de toast e o anexa ao body. */
  private ensureElement(): HTMLDivElement {
    const existing = this.element ?? this.findExisting();
    if (existing) {
      this.element = existing;
      return existing;
    }
    const created = document.createElement('div');
    created.className = MINI_TOAST_CLASS;
    created.id = MINI_TOAST_ID;
    document.body.appendChild(created);
    this.element = created;
    return created;
  }

  /** Reaproveita um toast pre-existente no DOM (ex.: deixado por shared.js legado). */
  private findExisting(): HTMLDivElement | null {
    return document.getElementById(MINI_TOAST_ID) as HTMLDivElement | null;
  }

  /** Reinicia o timer de ocultacao, garantindo um unico timer ativo por vez. */
  private restartHideTimer(toast: HTMLDivElement): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      toast.classList.remove('visible');
    }, MINI_TOAST_VISIBLE_MS);
  }
}

/**
 * Cria a implementacao de AppCommands.
 *
 * `openCopilotChat` aqui e o FALLBACK do scaffold: mostra o prompt no toast. O
 * modulo do Copiloto (port de assets/copilot-chat.js) sobrescreve
 * `window.openCopilotChat` com a implementacao real quando montar — por isso esta
 * versao nunca le `window.openCopilotChat` (evitaria recursao apos main.ts publicar).
 */
export function createAppCommands(): AppCommands {
  const toast = new MiniToast();
  return {
    sendPrompt: (text) => sendPromptViaToast(toast, text),
    openLink: (url) => openExternalLink(url),
    openCopilotChat: (text) => openCopilotChatFallback(toast, text),
  };
}

/** Dispara o toast de demonstracao (porte de window.sendPrompt em shared.js). */
function sendPromptViaToast(toast: MiniToast, text: string): void {
  if (typeof text !== 'string' || text.trim() === '') {
    throw new Error(
      `sendPrompt: texto invalido, recebido ${JSON.stringify(text)}, esperado string nao-vazia`,
    );
  }
  console.log('[Action simulada]:', text);
  toast.show(text);
}

/** Abre uma URL em nova aba (porte de window.openLink em shared.js). */
function openExternalLink(url: string): void {
  if (typeof url !== 'string' || url.trim() === '') {
    throw new Error(
      `openLink: url invalida, recebido ${JSON.stringify(url)}, esperado string nao-vazia`,
    );
  }
  window.open(url, '_blank');
}

/**
 * Fallback do scaffold para abrir o Copiloto: mostra o prompt no toast. Quando o
 * modulo real do Copiloto montar e sobrescrever window.openCopilotChat, este
 * fallback deixa de ser chamado pelos onclick inline.
 */
function openCopilotChatFallback(toast: MiniToast, text: string): void {
  const trimmed = typeof text === 'string' ? text.trim() : '';
  toast.show(trimmed === '' ? 'Abrir Copiloto IA' : trimmed);
}
